import { MediaKind } from "@/api/types";

import { EXPORT_SIZE, QuarterTurns, Size, Transform, destRect, orientedSize } from "./cropGeometry";

/**
 * Decoding a picked file, and writing back the framed result.
 *
 * <p>Re-encoding here is not only about size. Drawing through a canvas <b>discards EXIF</b>, and a
 * photo off a phone routinely carries the GPS coordinates of wherever it was taken. These images are
 * published on a public booking page, so passing the original bytes through would hand a host's home
 * address to every invitee who opened it.</p>
 *
 * <p>That reasoning is why {@link exportSource} exists in this file rather than the raw {@code File}
 * being uploaded as the reframe-later original. The kept copy travels to the same public bucket, so
 * it goes through the same canvas and arrives with the same metadata stripped.</p>
 *
 * <p>None of this is a security control — a hostile client sends whatever it likes, and the server's
 * limits and the signed content-length are unchanged. It means honest clients almost never meet a
 * limit, and that we do not publish metadata nobody intended to share.</p>
 */

/** WebP throughout: it keeps alpha, so a logo with a transparent background survives. */
const OUTPUT_TYPE = "image/webp";
const QUALITY = 0.9;

/**
 * Longest edge kept for the reframe-later original.
 *
 * <p>Enough to reframe a 1536-wide banner at full quality with room to crop into, and small enough
 * that the copy is a few hundred kilobytes rather than the multi-megabyte thing the host picked. It
 * is never displayed anywhere — its only job is to be re-croppable.</p>
 */
const SOURCE_MAX_EDGE = 2048;
const SOURCE_QUALITY = 0.85;

/**
 * Longest edge of the bitmap we actually work with while framing.
 *
 * <p>A modern phone photo is 50 megapixels, and a decoded bitmap is four bytes a pixel whatever the
 * file weighed — so an 8000x6000 shot is roughly 190MB of live pixels, held while the host drags it
 * about and re-encoded on every preview. Bounding the long edge cuts that to about 30MB.</p>
 *
 * <p>Comfortably above everything downstream: the widest export is 1536, and the kept original is
 * bounded to {@link SOURCE_MAX_EDGE} anyway, so nothing here is a limit a host can see. It only
 * stops us holding detail no output has a use for.</p>
 */
const DECODE_MAX_EDGE = 3200;

/** Whether the browser can decode and re-encode at all. */
export function cropSupported(): boolean {
  return typeof createImageBitmap === "function" && typeof document !== "undefined";
}

/**
 * Decode a picked file into a bitmap, with orientation already applied and the size bounded.
 *
 * <p>{@code imageOrientation: "from-image"} is not optional. Orientation lives in an EXIF flag that
 * canvas drawing ignores, so without it every portrait photo taken on a phone arrives on its side —
 * the classic bug in browser-side image handling, and one the host would blame on us.</p>
 */
export async function decode(file: File): Promise<ImageBitmap> {
  return bound(await decodeAnyFormat(file));
}

/**
 * Decode whatever the host picked, including the formats the browser will not touch.
 *
 * <p>Native first, always. Safari on a Mac or an iPhone decodes HEIC itself, and those are precisely
 * the devices HEIC files come from — so the common case costs nothing and downloads nothing. Only
 * when the browser refuses, and the file looks like HEIC, do we pay for a decoder.</p>
 */
async function decodeAnyFormat(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (refused) {
    if (!looksHeic(file)) throw refused;
    // Split out so it arrives as its own chunk, fetched the first time someone actually drops an
    // iPhone photo on a browser that cannot read one.
    const { decodeHeic } = await import("./heicDecode");
    return decodeHeic(file);
  }
}

/**
 * Whether a file is worth loading the HEIC decoder for.
 *
 * <p>Cheap and deliberately approximate — it decides whether to fetch a few megabytes of decoder,
 * not whether the file is valid. The decoder settles that itself, and a wrong guess here costs a
 * failed decode we were about to report anyway. The name is checked as well as the type because
 * browsers that cannot decode HEIC frequently cannot name it either, and hand us an empty type.</p>
 */
function looksHeic(file: File): boolean {
  return /hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

/**
 * Shrink an oversized bitmap, and release the original.
 *
 * <p>Best effort by design: if the browser will not resample, working with the full-size bitmap is
 * merely wasteful, and refusing the host's photo over it would be worse than wasteful.</p>
 */
async function bound(bitmap: ImageBitmap): Promise<ImageBitmap> {
  const longest = Math.max(bitmap.width, bitmap.height);
  if (longest <= DECODE_MAX_EDGE) return bitmap;

  const ratio = DECODE_MAX_EDGE / longest;
  try {
    const smaller = await createImageBitmap(bitmap, {
      resizeWidth: Math.round(bitmap.width * ratio),
      resizeHeight: Math.round(bitmap.height * ratio),
      resizeQuality: "high",
    });
    // Only once its replacement exists, or a failure here would leave the caller holding nothing.
    bitmap.close();
    return smaller;
  } catch {
    return bitmap;
  }
}

/**
 * Write out the frame the host arranged, at exactly the stored size for this kind.
 *
 * <p>The canvas is <b>not</b> painted before the image goes down, and that is the whole mechanism
 * behind a fitted banner sitting on white. Whatever the image does not cover stays transparent, so
 * the page it lands on supplies the colour — the card's background in light mode, the card's
 * background in dark mode, and never a slab of the wrong one baked in at upload time.</p>
 *
 * <p>Because the output ratio equals the frame's ratio, {@code object-cover} at render time has
 * nothing left to crop: what the host saw through the mask is what an invitee sees.</p>
 */
export async function exportCrop(
  bitmap: ImageBitmap,
  transform: Transform,
  frame: Size,
  kind: MediaKind,
  fileName: string,
  turns: QuarterTurns = 0,
): Promise<File> {
  const target = EXPORT_SIZE[kind];
  const image = { width: bitmap.width, height: bitmap.height };

  // One ratio carries the on-screen arrangement up to the stored size. The stage is measured from
  // the viewport and so is never a round number, which is exactly why the placement is computed in
  // frame pixels and converted once, here, rather than being tracked in two units.
  const k = target.width / frame.width;
  const rect = destRect(transform, orientedSize(image, turns), frame);

  const canvas = document.createElement("canvas");
  canvas.width = target.width;
  canvas.height = target.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is unavailable.");

  // Better downscaling than the default, which is visible on a photo reduced this far.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // The whole bitmap, placed. Anything outside the canvas is the crop, and the canvas clips it for
  // us — there is no source rectangle to get wrong, and no way to ask for pixels the image does not
  // have, which is what used to put a hairline of transparency around an avatar.
  //
  // Rotation happens about the placed rectangle's own CENTRE, which is what keeps this one line and
  // not a special case: a quarter turn about the centre leaves a bounding box of exactly the
  // oriented size the rectangle was computed from, so the drawing lands where the host arranged it.
  drawRotated(ctx, bitmap, {
    cx: (rect.x + rect.width / 2) * k,
    cy: (rect.y + rect.height / 2) * k,
    width: image.width * transform.scale * k,
    height: image.height * transform.scale * k,
    turns,
  });

  return encode(canvas, QUALITY, fileName);
}

/** Draw a bitmap centred on a point, turned. Sizes are the UNROTATED ones, as drawn. */
function drawRotated(
  ctx: CanvasRenderingContext2D,
  bitmap: ImageBitmap,
  at: { cx: number; cy: number; width: number; height: number; turns: QuarterTurns },
): void {
  ctx.save();
  ctx.translate(at.cx, at.cy);
  ctx.rotate((at.turns * Math.PI) / 2);
  ctx.drawImage(bitmap, -at.width / 2, -at.height / 2, at.width, at.height);
  ctx.restore();
}

/**
 * A modest, metadata-free copy of what the host picked, kept so framing can be changed later.
 *
 * <p>Without this, "adjust the position" means finding the original file again — and the file that
 * produced a banner six months ago is usually gone. Everything below is in service of keeping it
 * cheap enough to store for every upload: bounded to {@link SOURCE_MAX_EDGE}, re-encoded, and never
 * fetched by anyone but its owner opening the cropper.</p>
 *
 * <p>Rotation is baked in, unlike the framing, which is not. The difference is that a quarter turn
 * corrects the file — a photo that came off the camera sideways was never meant to be sideways — so
 * storing it turned means Adjust reopens on a picture that is the right way up, rather than making
 * the host fix the same thing every time they revisit it.</p>
 */
export async function exportSource(
  bitmap: ImageBitmap,
  fileName: string,
  turns: QuarterTurns = 0,
): Promise<File> {
  // Only ever shrinks. Enlarging a small image here would store interpolation as if it were detail,
  // and cost bytes to do it.
  const ratio = Math.min(1, SOURCE_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const oriented = orientedSize({ width: bitmap.width, height: bitmap.height }, turns);

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(oriented.width * ratio));
  canvas.height = Math.max(1, Math.round(oriented.height * ratio));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is unavailable.");

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  drawRotated(ctx, bitmap, {
    cx: canvas.width / 2,
    cy: canvas.height / 2,
    width: bitmap.width * ratio,
    height: bitmap.height * ratio,
    turns,
  });

  return encode(canvas, SOURCE_QUALITY, fileName);
}

async function encode(canvas: HTMLCanvasElement, quality: number, fileName: string): Promise<File> {
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, OUTPUT_TYPE, quality),
  );
  if (!blob) throw new Error("Could not encode the image.");
  return new File([blob], toWebpName(fileName), { type: OUTPUT_TYPE });
}

function toWebpName(name: string): string {
  const base = name.replace(/\.[^.]+$/, "");
  return `${base || "image"}.webp`;
}
